import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import UpdatePrompt from "./components/UpdatePrompt";
import Login from "./routes/Login";
import Overview from "./routes/Overview";
import Audit from "./routes/Audit";
import Emails from "./routes/Emails";
import Shell from "./components/Shell";
import SettingsEmail from "./routes/SettingsEmail";
import SettingsPayments from "./routes/SettingsPayments";
import SettingsAi from "./routes/SettingsAi";
import SettingsAccount from "./routes/SettingsAccount";
import Members from "./routes/Members";
import Identity from "./routes/Identity";
import Reports from "./routes/Reports";
import Fraud from "./routes/Fraud";
import Jobs from "./routes/Jobs";
import Contracts from "./routes/Contracts";

export interface AdminSession {
  adminId: string;
  email: string;
  displayName: string;
  role: string;
  markets: string[];
  /** true 代表還沒設定二步驗證，登入後只能去完成設定 */
  needsTotpEnrollment: boolean;
}

/**
 * 管理後台路由
 * ---------------------------------------------------------------
 * 沒有市場／語系前綴 —— 管理後台是內部工具，只有中文，
 * 而且管理員可以跨市場查看（權限由 admin_users.markets 控制）。
 *
 * 注意 IP 白名單已經在 Worker 層擋掉了未授權的連線，
 * 所以這裡的登入畫面只有通過白名單的人看得到。
 */
export default function App() {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/me", { credentials: "same-origin" });
      if (!res.ok) {
        setSession(null);
        return;
      }
      // 未登入時後端回 200 加 data: null —— 那是答案，不是錯誤
      setSession(((await res.json()).data as AdminSession | null) ?? null);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /*
    登出走這一條，不要再打一次 /api/me。

    原本 onSignOut 直接接 refresh，於是每次登出都會去問一次
    「我是誰」—— 而我們剛剛才親手把 cookie 清掉，答案必然是 401。

    那個 401 功能上無害（前端收到就顯示登入頁），但它每次都會在
    console 留一筆紅色錯誤。代價不是那一行字，是它會訓練人忽略
    console —— 真正的錯誤就被埋在同一堆紅字裡。這個後台才剛發生
    過一次：I18nProvider 沒掛上去讓登入頁整個當掉，而畫面上的
    第一個訊息長得跟這個 401 一模一樣。

    已經知道答案的問題就不要問。
  */
  const signedOut = useCallback(() => setSession(null), []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-page">
        <div className="h-2 w-40 overflow-hidden rounded-pill nm-inset-sm">
          <div className="h-full w-1/3 animate-pulse rounded-pill bg-brand-500" />
        </div>
      </div>
    );
  }

  if (!session) return <Login onSuccess={refresh} />;

  /*
   * 這裡以前有一段「還沒設定 TOTP 就強制去設定」的守衛。
   *
   * 二步驗證改成電子郵件之後不需要了：信箱在建立帳號時就存在，
   * 沒有「要登入才能設定、要設定才能登入」的死結，
   * 所以也不會有「已登入但還沒設定」這種中間狀態要處理。
   */

  return (
    <Shell session={session} onSignOut={signedOut}>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/members" element={<Members />} />
        <Route path="/identity" element={<Identity />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/fraud" element={<Fraud />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/contracts" element={<Contracts />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/emails" element={<Emails />} />
        <Route path="/settings/email" element={<SettingsEmail />} />
        <Route path="/settings/payments" element={<SettingsPayments />} />
        <Route path="/settings/ai" element={<SettingsAi />} />
        <Route path="/settings/account" element={<SettingsAccount session={session} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* 部署新版時提醒重新整理 —— 後台和前台一樣會卡在舊的 JS */}
      <UpdatePrompt />
    </Shell>
  );
}
